import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { TESTIMONIALS } from "@/data/content";

export default function Testimonials() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSel = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSel);
    onSel();
  }, [emblaApi]);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="border-b border-border py-24 relative overflow-hidden"
    >
      <div className="pointer-events-none absolute -bottom-10 right-10 h-52 w-52 rounded-full opacity-25 blur-3xl bg-[#FF3B30]/40"></div>

      <div className="mx-auto max-w-7xl px-4 sm:px-8 relative">
        <div className="flex items-center gap-3 font-jet text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
          <span>04</span><span className="h-px w-8 bg-border"></span><span>Testimonials</span>
        </div>

        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <h2 className="font-mono-brut font-black uppercase text-4xl sm:text-6xl tracking-tighter leading-none max-w-3xl">
            what founders <span className="text-[#E3FF00]">actually say.</span>
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={scrollPrev}
              aria-label="Previous testimonial"
              data-testid="testimonial-prev"
              className="w-11 h-11 border border-[#F4F4F0] flex items-center justify-center hover:bg-[#E3FF00] hover:text-[#0A0A0A] hover:border-[#E3FF00] transition"
            >
              <ArrowLeft size={16} />
            </button>
            <button
              type="button"
              onClick={scrollNext}
              aria-label="Next testimonial"
              data-testid="testimonial-next"
              className="w-11 h-11 border border-[#F4F4F0] flex items-center justify-center hover:bg-[#E3FF00] hover:text-[#0A0A0A] hover:border-[#E3FF00] transition"
            >
              <ArrowRight size={16} />
            </button>
          </div>
        </div>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                data-testid={`testimonial-card-${i}`}
                className="min-w-0 shrink-0 basis-full sm:basis-[85%] md:basis-[60%] lg:basis-[48%]"
              >
                <div
                  className="h-full border border-border bg-[#141414] p-8 sm:p-10 relative brut-hover"
                  style={{ boxShadow: `6px 6px 0 0 ${t.accent}` }}
                >
                  <Quote
                    size={40}
                    className="absolute -top-4 -left-2 opacity-90"
                    style={{ color: t.accent }}
                  />
                  <p className="mt-4 font-mono-brut text-lg sm:text-2xl leading-snug tracking-tight text-foreground">
                    {t.quote}
                  </p>
                  <div className="mt-8 pt-6 border-t border-border">
                    <div className="font-jet text-sm font-bold" style={{ color: t.accent }}>{t.author}</div>
                    <div className="font-jet text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className="flex items-center gap-2 mt-8">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollTo(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              data-testid={`testimonial-dot-${i}`}
              className={`h-2 transition-all ${
                selected === i ? "w-10 bg-[#E3FF00]" : "w-4 bg-border hover:bg-[#F4F4F0]/40"
              }`}
            />
          ))}
          <span className="font-jet text-[10px] uppercase tracking-widest text-muted-foreground ml-4">
            {String(selected + 1).padStart(2, "0")} / {String(TESTIMONIALS.length).padStart(2, "0")}
          </span>
        </div>

        <p className="mt-10 font-jet text-[10px] uppercase tracking-widest text-muted-foreground">
        </p>
      </div>
    </section>
  );
}
